import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api';
import { getUser } from '../auth';

const LAST_SYNC_KEY = 'eshara_last_successful_sync_at';

function formatTime(value) {
  if (!value) return 'لم تتم مزامنة بعد';

  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'غير معروف';
  }
}

function getRelativeTime(value) {
  if (!value) return 'لم تتم مزامنة بعد';

  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 'غير معروف';

  const diffMinutes = Math.max(0, Math.floor((Date.now() - time) / 60000));

  if (diffMinutes < 1) return 'الآن';
  if (diffMinutes < 60) return `منذ ${diffMinutes} دقيقة`;

  const hours = Math.floor(diffMinutes / 60);
  if (hours < 24) return `منذ ${hours} ساعة`;

  const days = Math.floor(hours / 24);
  return `منذ ${days} يوم`;
}

function ConnectionStatus() {
  const location = useLocation();
  const [isBrowserOnline, setIsBrowserOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [serverOnline, setServerOnline] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(() => {
    try {
      return localStorage.getItem(LAST_SYNC_KEY) || '';
    } catch {
      return '';
    }
  });
  const [expanded, setExpanded] = useState(false);

  const user = getUser();
  const isLoginPage = location.pathname === '/login';

  const status = useMemo(() => {
    if (!isBrowserOnline) {
      return {
        key: 'offline',
        label: 'لا يوجد اتصال بالإنترنت',
        hint: 'يمكنك قراءة الصفحات المحملة فقط',
        className: 'bg-red-600 text-white',
        dot: '🔴'
      };
    }

    if (!serverOnline) {
      return {
        key: 'server_offline',
        label: 'السيرفر غير متصل',
        hint: 'تأكد من تشغيل node server.js أو رابط Cloudflare',
        className: 'bg-orange-600 text-white',
        dot: '🟠'
      };
    }

    return {
      key: 'online',
      label: 'متصل',
      hint: 'المزامنة تعمل بشكل طبيعي',
      className: 'bg-green-600 text-white',
      dot: '🟢'
    };
  }, [isBrowserOnline, serverOnline]);

  const saveLastSync = () => {
    const now = new Date().toISOString();
    setLastSyncAt(now);

    try {
      localStorage.setItem(LAST_SYNC_KEY, now);
    } catch {}
  };

  const checkServer = async () => {
    if (!user || isLoginPage) return;

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsBrowserOnline(false);
      setServerOnline(false);
      return;
    }

    try {
      setChecking(true);
      const response = await apiFetch('/system-health');
      if (response && response.ok) {
        setServerOnline(true);
        saveLastSync();
      } else {
        setServerOnline(false);
      }
    } catch {
      setServerOnline(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsBrowserOnline(true);
      checkServer();
    };

    const handleOffline = () => {
      setIsBrowserOnline(false);
      setServerOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoginPage]);

  useEffect(() => {
    if (!user || isLoginPage) return;

    checkServer();
    const interval = setInterval(checkServer, 45000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoginPage]);

  if (!user || isLoginPage) {
    return null;
  }

  return (
    <div dir="rtl" className="fixed bottom-4 right-4 z-50 max-w-[calc(100vw-2rem)]">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={`${status.className} shadow-lg rounded-2xl px-4 py-3 font-bold text-sm flex items-center gap-2 border border-white/20`}
        title={status.hint}
      >
        <span>{status.dot}</span>
        <span>{status.label}</span>
        <span className="text-xs opacity-90">
          {checking ? 'يفحص...' : `آخر مزامنة: ${getRelativeTime(lastSyncAt)}`}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 bg-white border border-gray-200 shadow-xl rounded-2xl p-4 text-sm text-gray-800 w-[320px] max-w-full">
          <div className="font-bold text-gray-900 mb-2">📡 حالة الاتصال والمزامنة</div>

          <div className="space-y-2 leading-7">
            <p>
              <span className="font-bold">حالة النظام:</span>{' '}
              {status.label}
            </p>
            <p>
              <span className="font-bold">التفاصيل:</span>{' '}
              {status.hint}
            </p>
            <p>
              <span className="font-bold">آخر مزامنة ناجحة:</span>{' '}
              {formatTime(lastSyncAt)}
            </p>
          </div>

          <button
            type="button"
            onClick={checkServer}
            disabled={checking}
            className="mt-4 w-full bg-gray-900 hover:bg-black disabled:bg-gray-400 text-white rounded-xl py-2 font-bold"
          >
            {checking ? 'جاري الفحص...' : '🔄 فحص الآن'}
          </button>

          {status.key !== 'online' && (
            <div className="mt-3 bg-yellow-50 border border-yellow-100 text-yellow-800 rounded-xl p-3 leading-7">
              ملاحظة: لا تسجل عمليات مالية جديدة أثناء انقطاع الاتصال. سنجهز وضع القراءة بدون إنترنت بشكل أوسع في المرحلة التالية.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConnectionStatus;
